import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Search } from "lucide-react";

export interface TableEmptyStateProps {
  /** Plural noun for the rows, e.g. "customers". */
  entityNoun: string;
  /** Whether the search box currently holds a term. */
  hasSearch: boolean;
  /** `useTableQuery().activeFilterCount` — selected VALUES, not facet count. */
  activeFilterCount: number;
  /** Clears every facet. Rendered as the way out of an over-narrowed list. */
  onClearFilters: () => void;
  /**
   * Replacement heading for the GENUINELY-empty case only (no search, no
   * filters). Optional, because most lists want the generic "No X found";
   * a page whose emptiness needs explaining passes its own. The AI Prospects
   * page does — "No AI prospects yet" plus where they come from — and that
   * copy would be lost if the shared component always spoke for itself.
   *
   * Deliberately NOT applied to the search / filter branches: those sentences
   * exist to name the narrowing in force, which is the whole point of this
   * component, and a page-supplied override there would put the "your database
   * is empty" lie straight back.
   */
  emptyTitle?: string;
  /** Replacement body for the genuinely-empty case. See `emptyTitle`. */
  emptyBody?: string;
}

/**
 * The zero-rows state for a CRM list.
 *
 * It exists as a shared component because the sentence has to be TRUE, and the
 * obvious version isn't: branching on the search box alone, an empty result
 * caused by `support_level=24_7` reads "No customers found — no customers are
 * currently available in the system", which tells the viewer their database is
 * empty when in fact they have thousands of rows and one filter on. That is the
 * single commonest support complaint about filtered tables, and with nine list
 * pages inheriting this file it is worth stating once.
 *
 * So the copy names whichever narrowing is actually in force, and whenever a
 * filter is one of them it offers the way out — a list you cannot see cannot be
 * un-narrowed by a control you have scrolled past.
 */
export function TableEmptyState({
  entityNoun, hasSearch, activeFilterCount, onClearFilters, emptyTitle, emptyBody,
}: TableEmptyStateProps) {
  const hasFilters = activeFilterCount > 0;
  const filterWord = activeFilterCount === 1 ? "filter" : "filters";

  let title: string;
  let body: string;
  if (hasSearch && hasFilters) {
    title = "No results found";
    body = `No ${entityNoun} match this search with ${activeFilterCount} ${filterWord} applied. Try a different search, or clear the ${filterWord}.`;
  } else if (hasSearch) {
    title = "No results found";
    body = "Try adjusting your search criteria to find what you're looking for.";
  } else if (hasFilters) {
    title = `No ${entityNoun} match your filters`;
    body = `${activeFilterCount} ${filterWord} ${activeFilterCount === 1 ? "is" : "are"} active. Clear ${activeFilterCount === 1 ? "it" : "them"} to see the full list.`;
  } else {
    title = emptyTitle ?? `No ${entityNoun} found`;
    body = emptyBody ?? `No ${entityNoun} are currently available in the system.`;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, textAlign: "center" }}>
      <Box sx={{ mx: "auto", display: "flex", height: 64, width: 64, alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.primary.main, 0.1), mb: 2 }}>
        <Box sx={{ display: "flex", color: "primary.main" }}><Search size={32} /></Box>
      </Box>
      <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 3, maxWidth: 384 }}>
        {body}
      </Typography>
      {hasFilters && (
        <Button variant="outlined" size="small" onClick={onClearFilters}>
          Clear filters
        </Button>
      )}
    </Box>
  );
}
